import {
  Button,
  Dialog,
  DialogContent,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { useCallback, useState, useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { isEmpty } from 'lodash';
import Swal from 'sweetalert2';
import { useGlobalCore } from '../../../../core/modules/global';
import { UPDATE_AREA_DIALOG } from '../../../../core/constants/dialog';
import Spinner from '../../Spinner';
import { useCompanyCore } from '../../../../core/modules/company';
import { useBusinessCore } from '../../../../core/modules/business';
import {
  Business,
  BusinessArea,
} from '../../../../core/interfaces/business.interface';
// No se necesitan imports de validación de comisión

const createSchema = () => {
  return yup.object().shape({
    name: yup
      .string()
      .required('Campo requerido'),
    acronym: yup
      .string()
      .test('len', '3 caracteres requeridos', (val) => val?.length === 3)
      .required('Campo requerido'),
    commission: yup
      .number()
      .typeError('Debe ser un número')
      .min(0.1, 'Debe ser mayor a 0')
      .max(100, 'La comisión no puede ser mayor a 100%')
      .required('Campo requerido'),
  });
};

interface UpdateAreaDialogProps {
  selectedBusiness: Business | null;
  selectedArea: BusinessArea | null;
}

const UpdateAreaDialog: React.FC<UpdateAreaDialogProps> = ({
  selectedBusiness,
  selectedArea,
}) => {
  const { dialogs, closeDialog } = useGlobalCore();
  const { putArea, deleteArea, getBusinesses } = useBusinessCore();
  const { companyId } = useCompanyCore();

  const [loading, setLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [sinComision, setSinComision] = useState(false);

  // Verificar si es el área principal (mismo acrónimo que la empresa)
  const isMainArea = useMemo(() => {
    return Boolean(
      selectedBusiness &&
        selectedArea &&
        selectedArea.acronym === selectedBusiness.acronym
    );
  }, [selectedBusiness, selectedArea]);

  // Verificar si es la única área que queda
  const isOnlyArea = useMemo(() => {
    return Boolean(selectedBusiness?.areas?.length === 1);
  }, [selectedBusiness]);

  // No se puede eliminar si es área principal O si es la única área
  const cannotDelete = useMemo(() => {
    return isMainArea || isOnlyArea;
  }, [isMainArea, isOnlyArea]);

  const schema = useMemo(() => {
    return yup.object().shape({
      name: yup.string().required('Campo requerido'),
      acronym: yup
        .string()
        .test('len', '3 caracteres requeridos', (val) => val?.length === 3)
        .required('Campo requerido'),
      commission: sinComision
        ? yup.number().nullable()
        : yup
            .number()
            .typeError('Debe ser un número')
            .min(0.1, 'Debe ser mayor a 0')
            .required('Campo requerido'),
    });
  }, [sinComision]);

  const { control, formState, handleSubmit, resetField, watch, reset } =
    useForm({
      mode: 'onBlur',
      resolver: yupResolver(schema),
      defaultValues: {
        name: '',
        acronym: '',
        commission: 0,
      },
    });

  // Resetear formulario cuando cambie el área seleccionada
  useEffect(() => {
    if (selectedArea) {
      reset({
        name: selectedArea.name,
        acronym: selectedArea.acronym,
        commission: selectedArea.commission,
      });
      setSinComision(selectedArea.commission === 0);
    }
  }, [selectedArea, reset]);

  const { isValid, errors } = formState;

  // Observar los valores del formulario
  const watchedValues = watch();

  // Verificar si todos los campos requeridos están llenos
  const requiredFields: (keyof typeof watchedValues)[] = sinComision
    ? ['name', 'acronym']
    : ['name', 'acronym', 'commission'];
  const allRequiredFieldsFilled = requiredFields.every((field) => {
    const value = watchedValues[field];
    return (
      value !== undefined && value !== null && value.toString().trim() !== ''
    );
  });

  // Limpiar el campo commission cuando se marca "sin comisión"
  useEffect(() => {
    if (sinComision) {
      resetField('commission');
    }
  }, [sinComision, resetField]);

  const resetForm = useCallback(() => {
    resetField('name');
    resetField('acronym');
    resetField('commission');
    closeDialog(UPDATE_AREA_DIALOG);
  }, [closeDialog, resetField]);

  const onSubmit = useCallback(
    (data: any) => {
      if (companyId && selectedBusiness && selectedArea) {
        setLoading(true);
        putArea(
          companyId,
          selectedBusiness.id,
          selectedArea.id,
          {
            name: data.name,
            acronym: data.acronym,
            commission: sinComision ? 0 : Number(data.commission),
          },
          () => {
            resetForm();
            getBusinesses(companyId);
            setLoading(false);
          },
          () => {
            setLoading(false);
          }
        );
      }
    },
    [companyId, selectedBusiness, selectedArea, putArea, resetForm, getBusinesses, sinComision]
  );

  const handleDelete = useCallback(() => {
    if (!companyId || !selectedBusiness || !selectedArea) return;

    // Validar si no se puede eliminar (área principal o única área)
    if (cannotDelete) {
      let title = 'No se puede eliminar';
      let text = '';

      if (isMainArea && isOnlyArea) {
        text = `No se puede eliminar el área "${selectedArea.acronym}" porque es el área principal y la única área de la empresa "${selectedBusiness.name}". Una empresa debe tener al menos un área.`;
      } else if (isMainArea) {
        text = `No se puede eliminar el área "${selectedArea.acronym}" porque es el área principal de la empresa "${selectedBusiness.name}". Esta área representa a la empresa y siempre debe existir.`;
      } else if (isOnlyArea) {
        text = `No se puede eliminar el área "${selectedArea.acronym}" porque es la única área de la empresa "${selectedBusiness.name}". Una empresa debe tener al menos un área.`;
      }

      Swal.fire({
        title,
        text,
        icon: 'error',
        confirmButtonText: 'Entendido',
      });
      return;
    }

    Swal.fire({
      title: 'Confirmar eliminación',
      text: `¿Está seguro de eliminar el área "${selectedArea.acronym}"? Esta acción no se puede deshacer y la comisión (${selectedArea.commission}%) volverá a estar disponible para la empresa.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
    }).then((result) => {
      if (result.isConfirmed) {
        setIsDeleting(true);
        deleteArea(
          companyId,
          selectedBusiness.id,
          selectedArea.id,
          () => {
            resetForm();
            getBusinesses(companyId);
            setIsDeleting(false);
          },
          () => {
            setIsDeleting(false);
          }
        );
      }
    });
  }, [
    companyId,
    selectedBusiness,
    selectedArea,
    deleteArea,
    resetForm,
    getBusinesses,
    cannotDelete,
    isMainArea,
    isOnlyArea,
  ]);

  return (
    <Dialog
      classes={{
        paper: 'm-8 sm:m-24 min-w-[350px] md:min-w-[500px]',
      }}
      onClose={resetForm}
      open={Array.isArray(dialogs) && dialogs.includes(UPDATE_AREA_DIALOG)}
    >
      <DialogContent>
        <div className="flex flex-auto flex-col py-16 px-0 sm:px-16">
          <div className="flex flex-col">
            <div className="mb-16 flex items-center">
              <Typography className="text-2xl font-bold truncate">
                Editar Área
              </Typography>
            </div>

            {selectedBusiness && selectedArea && (
              <div className="mb-24 p-16 bg-gray-50 rounded-lg">
                <Typography variant="body1" className="text-gray-800 mb-8">
                  Empresa: <strong>{selectedBusiness.name}</strong>
                </Typography>
                <Typography variant="body2" className="text-gray-600">
                  Editando área: <strong>{selectedArea.acronym}</strong>
                </Typography>
              </div>
            )}

            {cannotDelete && (
              <div className="mb-16 p-16 bg-blue-50 border border-blue-200 rounded-lg">
                <Typography variant="body2" className="text-blue-800">
                  {isMainArea && isOnlyArea ? (
                    <>
                      🏢 <strong>Área Principal y Única:</strong> Esta es el
                      área principal y la única área de la empresa "
                      {selectedBusiness?.name}". No se puede eliminar porque
                      representa a la empresa y es su única área.
                    </>
                  ) : isMainArea ? (
                    <>
                      🏢 <strong>Área Principal:</strong> Esta área representa a
                      la empresa y tiene el mismo acrónimo (
                      {selectedArea?.acronym}). No se puede eliminar ni cambiar
                      su acrónimo desde aquí. Para modificar el acrónimo, debe
                      editarlo desde la empresa y se sincronizará
                      automáticamente.
                    </>
                  ) : (
                    <>
                      ⚠️ <strong>Última Área:</strong> Esta es la única área
                      restante de la empresa "{selectedBusiness?.name}". No se
                      puede eliminar porque una empresa debe tener al menos un
                      área.
                    </>
                  )}
                </Typography>
              </div>
            )}

            <div className="w-full max-w-xl mt-20">
              <div className="flex flex-col space-y-32">
                <form noValidate onSubmit={handleSubmit(onSubmit)}>
                  <div className="mb-24">
                    <Controller
                      name="name"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Nombre del Área"
                          type="text"
                          variant="outlined"
                          disabled={loading || isDeleting}
                          error={!!errors.name}
                          helperText={errors?.name?.message}
                          required
                          fullWidth
                        />
                      )}
                    />
                  </div>

                  <div className="mb-24">
                    <Controller
                      name="acronym"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Sigla del Área"
                          type="text"
                          variant="outlined"
                          disabled={loading || isDeleting || isMainArea}
                          error={!!errors.acronym}
                          helperText={
                            isMainArea
                              ? 'La sigla del área principal debe coincidir con la empresa. Edítela desde la empresa.'
                              : errors?.acronym?.message
                          }
                          required
                          fullWidth
                          inputProps={{ maxLength: 3 }}
                        />
                      )}
                    />
                  </div>

                  {!sinComision && (
                    <div className="mb-24">
                      <Controller
                        name="commission"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Comisión (%)"
                            type="number"
                            variant="outlined"
                            disabled={loading || isDeleting}
                            error={!!errors.commission}
                            helperText={
                              errors?.commission?.message ||
                              'Ingrese el porcentaje de comisión para esta área'
                            }
                            required
                            fullWidth
                            inputProps={{
                              min: 0.1,
                              max: 100,
                              step: 0.1,
                            }}
                            onChange={(e) => {
                              const value = e.target.value;
                              // Validar que no sea mayor a 100
                              if (value === '' || (parseFloat(value) >= 0.1 && parseFloat(value) <= 100)) {
                                field.onChange(value);
                              }
                            }}
                          />
                        )}
                      />
                    </div>
                  )}

                  <div className="mb-24">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={sinComision}
                          onChange={(e) => setSinComision(e.target.checked)}
                        />
                      }
                      label="Sin comisión"
                    />
                  </div>

                  <div className="flex justify-center gap-16">
                    <Button
                      variant="outlined"
                      color="error"
                      sx={{ borderRadius: 50 }}
                      onClick={handleDelete}
                      disabled={loading || isDeleting || cannotDelete}
                      size="large"
                      title={
                        cannotDelete
                          ? isMainArea && isOnlyArea
                            ? 'No se puede eliminar: área principal y única de la empresa'
                            : isMainArea
                            ? 'No se puede eliminar: área principal de la empresa'
                            : 'No se puede eliminar: última área de la empresa'
                          : 'Eliminar área'
                      }
                    >
                      <Spinner show={isDeleting} />
                      {cannotDelete ? 'No se puede eliminar' : 'Eliminar'}
                    </Button>
                    <Button
                      variant="outlined"
                      color="secondary"
                      sx={{ borderRadius: 50 }}
                      onClick={resetForm}
                      disabled={loading || isDeleting}
                      size="large"
                    >
                      Cancelar
                    </Button>
                    <Button
                      variant="contained"
                      color="secondary"
                      sx={{ borderRadius: 50 }}
                      disabled={
                        !isEmpty(errors) ||
                        !allRequiredFieldsFilled ||
                        loading ||
                        isDeleting
                      }
                      type="submit"
                      size="large"
                    >
                      <Spinner show={loading} />
                      Actualizar
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UpdateAreaDialog;
